import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import api from '../../api/client';
import { SessionDto, MenuItemDto, BillSummaryDto } from '../../types';
import './Session.css';

const SessionDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [session, setSession] = useState<SessionDto | null>(null);
  const [menuItems, setMenuItems] = useState<MenuItemDto[]>([]);
  const [summary, setSummary] = useState<BillSummaryDto | null>(null);
  const [loading, setLoading] = useState(true);

  // Add member
  const [memberType, setMemberType] = useState<'user' | 'guest'>('guest');
  const [guestName, setGuestName] = useState('');
  const [userId, setUserId] = useState('');

  // Add item
  const [itemSource, setItemSource] = useState<'menu' | 'custom'>('menu');
  const [selectedMenuItem, setSelectedMenuItem] = useState('');
  const [customName, setCustomName] = useState('');
  const [customPrice, setCustomPrice] = useState('');
  const [itemQty, setItemQty] = useState('1');

  // Assign
  const [assignItemId, setAssignItemId] = useState('');
  const [assignMemberId, setAssignMemberId] = useState('');
  const [assignQty, setAssignQty] = useState('1');

  // OCR
  const [ocrFile, setOcrFile] = useState<File | null>(null);
  const [ocrLoading, setOcrLoading] = useState(false);

  const fetchSession = useCallback(async () => {
    try {
      const res = await api.get(`/sessions/${id}`);
      setSession(res.data);
      if (res.data.restaurantId) {
        const menuRes = await api.get(`/restaurants/${res.data.restaurantId}/menu`);
        setMenuItems(menuRes.data);
      }
    } catch (err) {
      console.error('Failed to fetch session', err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchSession();
  }, [fetchSession]);

  const fetchSummary = async () => {
    try {
      const res = await api.get(`/sessions/${id}/summary`);
      setSummary(res.data);
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to get summary');
    }
  };

  const addMember = async () => {
    try {
      await api.post(`/sessions/${id}/members`, {
        userId: memberType === 'user' ? userId : undefined,
        guestName: memberType === 'guest' ? guestName : undefined,
      });
      setGuestName('');
      setUserId('');
      fetchSession();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to add member');
    }
  };

  const addItem = async () => {
    try {
      await api.post(`/sessions/${id}/items`, {
        menuItemId: itemSource === 'menu' ? parseInt(selectedMenuItem) : undefined,
        customItemName: itemSource === 'custom' ? customName : undefined,
        customItemPrice: itemSource === 'custom' ? parseFloat(customPrice) : undefined,
        quantity: parseInt(itemQty),
      });
      setSelectedMenuItem('');
      setCustomName('');
      setCustomPrice('');
      setItemQty('1');
      fetchSession();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to add item');
    }
  };

  const assignItem = async () => {
    try {
      await api.post(`/sessions/${id}/items/${assignItemId}/assign`, {
        sessionMemberId: parseInt(assignMemberId),
        quantity: parseInt(assignQty),
      });
      setAssignItemId('');
      setAssignMemberId('');
      setAssignQty('1');
      fetchSession();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to assign item');
    }
  };

  const closeSession = async () => {
    if (!window.confirm('Close this session? It will become read-only.')) return;
    try {
      await api.post(`/sessions/${id}/close`);
      fetchSession();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to close session');
    }
  };

  const handleOcrScan = async () => {
    if (!ocrFile) return;
    setOcrLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', ocrFile);
      const res = await api.post('/ocr/scan-receipt', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (res.data.success && res.data.items.length > 0) {
        for (const item of res.data.items) {
          await api.post(`/sessions/${id}/items`, {
            customItemName: item.name,
            customItemPrice: item.price,
            quantity: item.quantity,
          });
        }
        fetchSession();
        alert(`${res.data.items.length} items added from receipt!`);
      } else {
        alert('No items detected in the receipt');
      }
    } catch (err: any) {
      alert(err.response?.data?.message || 'OCR scan failed');
    } finally {
      setOcrLoading(false);
      setOcrFile(null);
    }
  };

  const copyBillLink = (memberId: number) => {
    const link = `${window.location.origin}/bill/${id}/${memberId}`;
    navigator.clipboard.writeText(link)
      .then(() => alert('Bill link copied!'))
      .catch(() => alert('Failed to copy link. Please copy manually: ' + link));
  };

  if (loading) return <p className="loading">Loading...</p>;
  if (!session) return <p className="loading">Session not found</p>;

  const isOpen = session.status === 'Open';

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1>Session #{session.id}</h1>
          <p className="session-meta">
            {session.restaurantName ?? 'No restaurant'} | {session.status} | {new Date(session.createdAt).toLocaleDateString()}
          </p>
        </div>
        {isOpen && (
          <button className="btn-danger" onClick={closeSession}>Close Session</button>
        )}
      </div>

      {/* Members Section */}
      <div className="section">
        <h2>Members ({session.members.length})</h2>
        <div className="members-list">
          {session.members.map((m) => (
            <div key={m.id} className="member-chip">
              {m.displayName}
              <button className="btn-link" onClick={() => copyBillLink(m.id)} title="Copy bill link">
                Share
              </button>
            </div>
          ))}
        </div>
        {isOpen && (
          <div className="inline-form">
            <select value={memberType} onChange={(e) => setMemberType(e.target.value as 'user' | 'guest')}>
              <option value="guest">Guest</option>
              <option value="user">User ID</option>
            </select>
            {memberType === 'guest' ? (
              <input placeholder="Guest name" value={guestName} onChange={(e) => setGuestName(e.target.value)} />
            ) : (
              <input placeholder="User ID" value={userId} onChange={(e) => setUserId(e.target.value)} />
            )}
            <button className="btn-primary btn-sm" onClick={addMember}>Add</button>
          </div>
        )}
      </div>

      {/* Items Section */}
      <div className="section">
        <h2>Items ({session.items.length})</h2>
        <div className="items-table">
          {session.items.map((item) => (
            <div key={item.id} className="item-row">
              <div className="item-info">
                <strong>{item.itemName}</strong>
                <span className="item-price">{item.itemPrice.toFixed(2)} x{item.quantity}</span>
              </div>
              <div className="item-assignments">
                {item.assignments.length > 0 ? (
                  item.assignments.map((a) => (
                    <span key={a.id} className="assignment-chip">
                      {a.memberName} (x{a.quantity})
                    </span>
                  ))
                ) : (
                  <span className="no-assignment">Not assigned</span>
                )}
              </div>
            </div>
          ))}
        </div>

        {isOpen && (
          <>
            {/* Add Item */}
            <div className="inline-form">
              <select value={itemSource} onChange={(e) => setItemSource(e.target.value as 'menu' | 'custom')}>
                <option value="menu">From Menu</option>
                <option value="custom">Custom Item</option>
              </select>
              {itemSource === 'menu' ? (
                <select value={selectedMenuItem} onChange={(e) => setSelectedMenuItem(e.target.value)}>
                  <option value="">Select item...</option>
                  {menuItems.map((m) => (
                    <option key={m.id} value={m.id}>{m.name} - {m.price.toFixed(2)}</option>
                  ))}
                </select>
              ) : (
                <>
                  <input placeholder="Item name" value={customName} onChange={(e) => setCustomName(e.target.value)} />
                  <input placeholder="Price" type="number" step="0.01" value={customPrice} onChange={(e) => setCustomPrice(e.target.value)} />
                </>
              )}
              <input placeholder="Qty" type="number" min="1" value={itemQty} onChange={(e) => setItemQty(e.target.value)} style={{ width: '70px' }} />
              <button className="btn-primary btn-sm" onClick={addItem}>Add Item</button>
            </div>

            {/* Assign Item */}
            {session.items.length > 0 && session.members.length > 0 && (
              <div className="inline-form">
                <select value={assignItemId} onChange={(e) => setAssignItemId(e.target.value)}>
                  <option value="">Assign item...</option>
                  {session.items.map((i) => (
                    <option key={i.id} value={i.id}>{i.itemName}</option>
                  ))}
                </select>
                <select value={assignMemberId} onChange={(e) => setAssignMemberId(e.target.value)}>
                  <option value="">To member...</option>
                  {session.members.map((m) => (
                    <option key={m.id} value={m.id}>{m.displayName}</option>
                  ))}
                </select>
                <input placeholder="Qty" type="number" min="1" value={assignQty} onChange={(e) => setAssignQty(e.target.value)} style={{ width: '70px' }} />
                <button className="btn-primary btn-sm" onClick={assignItem}>Assign</button>
              </div>
            )}

            {/* OCR Scan */}
            <div className="ocr-section">
              <h3>Scan Receipt</h3>
              <div className="inline-form">
                <input type="file" accept="image/*" onChange={(e) => setOcrFile(e.target.files?.[0] || null)} />
                <button className="btn-primary btn-sm" onClick={handleOcrScan} disabled={!ocrFile || ocrLoading}>
                  {ocrLoading ? 'Scanning...' : 'Scan & Add Items'}
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Summary */}
      <div className="section">
        <button className="btn-primary" onClick={fetchSummary}>View Bill Summary</button>
        {summary && (
          <div className="summary-section">
            <h2>Bill Summary</h2>
            <p>Delivery: {summary.deliveryFee.toFixed(2)} | Tip: {summary.tip.toFixed(2)} | Members: {summary.totalMembers}</p>
            {summary.memberBills.map((bill) => (
              <div key={bill.memberId} className="member-bill">
                <h3>{bill.memberName}</h3>
                <table>
                  <thead>
                    <tr><th>Item</th><th>Price</th><th>Qty</th><th>Shared</th><th>Your Share</th></tr>
                  </thead>
                  <tbody>
                    {bill.items.map((item, idx) => (
                      <tr key={idx}>
                        <td>{item.itemName}</td>
                        <td>{item.unitPrice.toFixed(2)}</td>
                        <td>{item.quantity}</td>
                        <td>{item.sharedBetween}</td>
                        <td>{item.yourShare.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="bill-totals">
                  <div>Items: {bill.itemsTotal.toFixed(2)}</div>
                  <div>Delivery: {bill.deliveryShare.toFixed(2)}</div>
                  <div>Tip: {bill.tipShare.toFixed(2)}</div>
                  <div className="grand-total">Total: {bill.grandTotal.toFixed(2)}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SessionDetail;
